import { watch, ref, computed } from 'vue';
import api from '@/api';
import cookie from 'shared-lib/helpers/cookie';
import Message from 'shared-lib/helpers/message';
import queryString from 'query-string';
import readonly from '@/helpers/readonly';
import { getConfig } from '@/config';
import {
    getCurrentUser,
    resetCurrentUser,
} from '@/store/user';
import {
    getDictionaries,
    getCountriesDictionary,
    getCategoriesDictionary,
    getApprovalStatuses,
} from '@/store/dictionaries';

const initialState = {
    isReady: false,
    isLoaded: false,
    isLogged: false,
    token_type: null,
    expires_in: null,
    access_token: null,
    refresh_token: null,
    errors: [],
};

const state = ref({ ...initialState });
export default computed(() => readonly(state.value));

export const initOttAuth = () => {
    const query = queryString.parse(location.search);
    const ott = query.ott;
    if (ott) {
        clearAuth();
        return api.ottLogin(ott).then((tokenData) => setTokenCookie(tokenData?.data || tokenData));
    } else {
        return Promise.resolve();
    }
};

export const initAuth = () => {
    const tokenData = getToken();

    if (tokenData) {
        if (tokenData.config) delete tokenData.config.data;
        let shortTokenData = tokenData?.data || tokenData;

        setTokenCookie(shortTokenData);
        authReady(shortTokenData);

        return getInitialData()
            .then(() => authLoaded())
            .catch((error) => {
                authLoaded();
                Message.apiError({ message: error.message });
                console.error(error); // eslint-disable-line no-console
                throw (error);
            });
    } else {
        getConfig()
            .then(() => authLoaded());
    }
};

export const authReady = (tokenData) => {
    state.value = {
        ...state.value,
        ...tokenData,
        isReady: true,
        isLogged: true,
        errors: [],
    };
};

export const authLoaded = () => {
    state.value.isLoaded = true;
};

export const authError = (error) => {
    state.value = {
        ...initialState,
        isReady: true,
        isLoaded: true,
        errors: error,
    };
};

export const logout = () => {
    return api.logout()
        .finally(() => {
            clearAuth();
            authLoaded();
        });
};

export const login = (credentials) => {
    clearCookie();

    return api.login(credentials)
        .then((tokenData) => {
            if (tokenData) {
                tokenData = tokenData.data || tokenData;
                setTokenCookie(tokenData);
                authReady(tokenData);

                getInitialData();
            }
        })
        .catch((error) => {
            authError(error);
            throw (error);
        });
};

export const loginWithGoogle = () => {
    return api.googleRedirect()
        .then((response) => {
            const url = response?.data?.data?.url;
            window.location.href = url;
        });
};

export const loginWithGoogleCallback = (params) => {
    return api.googleCallback({ ...params, client_id: process.env.VUE_APP_CLIENT_ID })
        .then((tokenData) => {
            if (tokenData) {
                tokenData = tokenData.data || tokenData;
                setTokenCookie(tokenData);
                authReady(tokenData);

                getInitialData();
            }
        })
        .catch((error) => {
            authError(error);
            throw (error);
        });
};

export const register = (data) => {
    clearAuth();
    authLoaded();

    return api.register(data)
        .catch((error) => {
            authError(error);
            throw (error);
        });
};

export const getToken = () => {
    const tokenCookie = cookie.get('token');
    return tokenCookie ? JSON.parse(tokenCookie) : null;
};

export const clearAuth = () => {
    state.value = {
        ...initialState,
        isReady: true,
    };
    resetCurrentUser();
    clearCookie();
};

export const clearCookie = () => {
    cookie.remove('token', { path: '/', domain: window.location.hostname });
};

export const setTokenCookie = (tokenData) => {
    if (tokenData) {
        const currentDate = Date.now();
        const expiresAt = Number(currentDate) + (Number(tokenData.expires_in) * 1000);

        let cookieOptions = {
            domain: window.location.hostname,
            path: '/',
            expires: new Date(expiresAt),
            secure: process.env.NODE_ENV === 'production',
            sameSite: process.env.NODE_ENV === 'production' ? 'None' : 'Lax',
        };

        cookie.set(
            'token',
            JSON.stringify(tokenData),
            cookieOptions
        );
    }
};

export const getInitialData = () => {
    return Promise.all([
        getConfig(),
        getCurrentUser(),
        getDictionaries(),
        getCountriesDictionary(),
        getCategoriesDictionary(),
        getApprovalStatuses(),
    ]);
};

export const ready = () => {
    return new Promise((resolve) => {
        watch(() => state.value.isLoaded, (isLoaded) => {
            if (isLoaded) {
                resolve();
            }
        }, { immediate: true });
    });
};
